import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@goaltree/ui/components/alert-dialog'
import { useMutation } from 'convex/react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { TaskContextMenu } from '@/components/task-context-menu'
import { useDeleteNode } from '@/hooks/use-delete-node'
import { useMustDo } from '@/hooks/use-must-do'
import { useOnlineStatus } from '@/hooks/use-online-status'
import { optimisticTaskStatus } from '@/lib/goals-optimistic'
import { patchNode } from '@/lib/nodes-optimistic'
import { useOfflineStore } from '@/store/offline.store'

export type GoalMenuTask = {
  _id: Id<'nodes'>
  title: string
  effort: 'low' | 'medium' | 'high'
  superUrgent?: boolean
  status: 'backlog' | 'in-progress' | 'done'
  completedAt: number | null
  mustDoOn?: string
  isHabit?: boolean
}

// The Everyday task menu for a goal-page task. No pin: pinning only reorders the
// Everyday tabs. Must-do is the owner's own lane, so shared-goal editors and
// coaches don't get it; coaches can't complete a client's task either.
export function GoalTaskMenu({
  pos,
  onClose,
  task,
  isDone,
  isInProgress,
  isOwner,
  viewerIsCoach,
  onToggleDone,
  onDetails,
  onRename,
  onDelete,
}: {
  pos: { x: number; y: number }
  onClose: () => void
  task: GoalMenuTask
  isDone: boolean
  isInProgress: boolean
  isOwner: boolean
  viewerIsCoach: boolean
  onToggleDone: () => void
  onDetails: () => void
  onRename?: () => void
  onDelete: () => void
}) {
  const online = useOnlineStatus()
  const enqueue = useOfflineStore((s) => s.enqueue)
  const mustDo = useMustDo()
  const setTaskStatus = useMutation(api.nodes.setTaskStatus).withOptimisticUpdate(
    optimisticTaskStatus,
  )
  const updateEffort = useMutation(api.nodes.updateTaskEffort).withOptimisticUpdate(
    (store, { taskId, effort, superUrgent }) =>
      patchNode(store, taskId, { effort, superUrgent: superUrgent || null }),
  )
  const isUrgent = task.effort === 'high'
  const isSuper = isUrgent && !!task.superUrgent
  const setEffort = (effort: 'low' | 'medium' | 'high', superUrgent = false) => {
    if (online) updateEffort({ taskId: task._id, effort, superUrgent }).catch(() => {})
    else enqueue({ kind: 'setEffort', args: { taskId: task._id as string, effort, superUrgent } })
  }
  const setStatus = (status: 'backlog' | 'in-progress') => {
    if (online) setTaskStatus({ taskId: task._id, status }).catch(() => {})
    else enqueue({ kind: 'setTaskStatus', args: { taskId: task._id as string, status } })
  }

  return (
    <TaskContextMenu
      pos={pos}
      onClose={onClose}
      title={task.title}
      canPin={false}
      canComplete={!viewerIsCoach}
      isDone={isDone}
      isInProgress={isInProgress}
      isUrgent={isUrgent}
      isSuperUrgent={isSuper}
      isPinned={false}
      onInProgress={() => setStatus(isInProgress ? 'backlog' : 'in-progress')}
      onPin={() => {}}
      onComplete={onToggleDone}
      onUrgent={() => setEffort(isUrgent ? 'low' : 'high')}
      onSuperUrgent={() => (isSuper ? setEffort('high') : setEffort('high', true))}
      onDetails={onDetails}
      onRename={onRename}
      onDelete={onDelete}
      mustDo={
        isOwner && !task.isHabit && !isDone
          ? { committed: task.mustDoOn != null, onCommit: (day) => void mustDo(task._id, day) }
          : undefined
      }
    />
  )
}

export function DeleteTaskDialog({
  task,
  onClose,
}: {
  task: { _id: Id<'nodes'>; title: string } | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const deleteNode = useDeleteNode()
  return (
    <AlertDialog open={task !== null} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('common.deleteTaskTitle')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('common.deleteTaskDesc', { title: task?.title ?? '' })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            onClick={() => {
              if (task)
                deleteNode(task._id).catch(() =>
                  toast.error("Couldn't delete task — please try again."),
                )
              onClose()
            }}
          >
            {t('common.delete')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
